import type { ApiRoomJSON } from 'mastodon/api_types/rooms';

/**
 * Client-side mirror of `Room#untraced?` (app/models/room.rb).
 *
 * A room leaves no public trace when the host marked it private OR it lives in
 * a private community: no announcement status, therefore no go-live
 * notification and NO RECORDING — there is nowhere for a recording to be
 * attached, and a private room is not meant to leave one behind.
 *
 * Keep this in step with the model. Ticket 30 happened because three separate
 * gates each re-implemented half of this rule (the community half) and drifted
 * from the model, so a private room recorded, showed a REC badge next to its
 * own Private badge, and wasted an upload the server always refused.
 */
export const isUntracedRoom = (
  room: ApiRoomJSON | null | undefined,
): boolean => {
  if (!room) return false;
  // Servers that predate the setting send no `visibility` at all — those rooms
  // are public, matching `visibilityOf` in room_visibility_toggle.
  return room.visibility === 'private' || room.community?.privacy === 'private';
};

/**
 * Whether the host-side announcement recorder may run for this room. Video
 * rooms are exempt for a different reason: stream VODs are heavy and are
 * deferred to a future premium feature.
 */
export const canRecordRoom = (room: ApiRoomJSON | null | undefined): boolean =>
  !!room && room.kind !== 'video' && !isUntracedRoom(room);

/**
 * Whether the REC badge belongs on screen. The badge must never contradict the
 * internal state: an untraced room does not record, so it shows no badge even
 * if a stale server flag or a recorder started before the room went private
 * still claims otherwise.
 */
export const shouldShowRecBadge = (
  room: ApiRoomJSON | null | undefined,
  recorderIsRecording: boolean,
): boolean => {
  if (!room || isUntracedRoom(room)) return false;
  return recorderIsRecording || room.recording === true;
};
